// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Outsize — turn a country's bundled lon/lat rings into an SVG silhouette path,
// fitted to its own bounding box with an equirectangular projection corrected for
// latitude (x scaled by cos(centroid lat)) so shapes are not stretched. Pure.

import type { Country } from './data';

export interface Silhouette {
  /** viewBox width/height (aspect-preserving; the larger axis is `SIZE`). */
  w: number;
  h: number;
  /** SVG path data (one subpath per ring). */
  d: string;
}

const SIZE = 100;
const PAD = 4;

/** Build the silhouette for a country, or a tiny fallback box if it has no rings. */
export function silhouette(country: Country): Silhouette {
  const rings = country.rings;
  if (!rings.length) return { w: SIZE, h: SIZE, d: '' };

  const latRad = (country.c[1] * Math.PI) / 180;
  const kx = Math.max(0.15, Math.cos(latRad)); // horizontal squeeze toward the poles

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const projected: Array<Array<[number, number]>> = [];
  for (const ring of rings) {
    const pts: Array<[number, number]> = [];
    for (let i = 0; i < ring.length; i += 2) {
      const px = ring[i] * kx;
      const py = -ring[i + 1]; // SVG y grows downward; latitude grows upward
      pts.push([px, py]);
      if (px < minX) minX = px;
      if (px > maxX) maxX = px;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
    }
    projected.push(pts);
  }

  const spanX = Math.max(1e-6, maxX - minX);
  const spanY = Math.max(1e-6, maxY - minY);
  const scale = (SIZE - PAD * 2) / Math.max(spanX, spanY);
  const w = spanX * scale + PAD * 2;
  const h = spanY * scale + PAD * 2;

  let d = '';
  for (const pts of projected) {
    for (let i = 0; i < pts.length; i++) {
      const x = (pts[i][0] - minX) * scale + PAD;
      const y = (pts[i][1] - minY) * scale + PAD;
      d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    }
    d += 'Z';
  }
  return { w: Math.round(w), h: Math.round(h), d };
}
